import React, { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetClose } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Share2, Clock, Mail, AlertCircle, FileText, RefreshCw, CloudUpload, X, Loader2, Flag } from 'lucide-react';
import { toast } from 'sonner';
import AssessmentRecording from '../assessment/AssessmentRecording';
import AssessmentSummary from '../assessment/AssessmentSummary';
import PerformanceComparison from '../assessment/PerformanceComparison';
import AssessmentTranscript from '../assessment/AssessmentTranscript';
import { ExtendedCandidate, CandidateAssessment } from '@/hooks/useSelectCandidate';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { encodeId } from '@/utils/idEncoder';
import { sendInvitationEmail } from '@/services/emailService';
import { encryptCandidateDetails } from '@/utils/encryption';
import { supabase, publicSupabase, serviceSupabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthContext';
import { pushToATS } from '@/services/knitService';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface CandidateSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidate: ExtendedCandidate;
  onRefresh?: () => void;
  candidateAssessment: CandidateAssessment | null;
}

const CandidateSheet: React.FC<CandidateSheetProps> = ({
  open,
  onOpenChange,
  candidate,
  onRefresh,
  candidateAssessment
}) => {
  const { user } = useAuth();
  const [showTranscript, setShowTranscript] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [resendingInvitation, setResendingInvitation] = useState(false);
  const [pushingToATS, setPushingToATS] = useState(false);
  const [isProcessingUpdate, setIsProcessingUpdate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [savingFitStatus, setSavingFitStatus] = useState(false);

  const handlePlayPause = () => setIsPlaying(!isPlaying);
  const handleVolumeToggle = () => setIsMuted(!isMuted);
  const handleOpenTranscript = () => setShowTranscript(true);
  const handleSeek = (value: number[]) => {
    if (value.length > 0) {
      setCurrentTime(value[0]);
    }
  };

  const handleShareAssessment = async () => {
    if (!candidate) return;
    
    try {
      const encodedId = encodeId(candidate.id);
      const publicUrl = `${window.location.origin}/candidate/${encodedId}`;
      
      try {
        // Try to shorten the URL first
        const { shortenUrl } = await import('@/services/url-shortener');
        
        // Set expiration to 30 days (in seconds)
        const expiresIn = 30 * 24 * 60 * 60;
        
        // Try to shorten the URL
        const shortUrl = await shortenUrl(publicUrl, expiresIn);
        
        // Copy the shortened URL to clipboard
        await navigator.clipboard.writeText(shortUrl);
        toast.success('Shortened link copied to clipboard');
      } catch (shortenError) {
        console.error('Failed to shorten public link:', shortenError);
        
        // Fall back to the original URL
        await navigator.clipboard.writeText(publicUrl);
        toast.success('Public link copied to clipboard');
      }
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const handlePushToATS = async () => {
    if (!candidate || !candidate.id) return;
    
    try {
      setPushingToATS(true);
      await pushToATS(candidate.id);
      toast.success('Successfully pushed assessment results to ATS');
    } catch (error: any) {
      console.error('Error pushing to ATS:', error);
      toast.error(error.message || 'Failed to push to ATS');
    } finally {
      setPushingToATS(false);
    }
  };

  const handleResendInvitation = async () => {
    if (!candidate || !candidate.assessment_id) return;
    
    try {
      setResendingInvitation(true);
      
      // Get assessment data including organization name
      const { data: assessmentData, error: assessmentError } = await supabase
        .from('assessments')
        .select(`
          *,
          organizations:organization_id (
            name
          )
        `)
        .eq('id', candidate.assessment_id)
        .single();
      
      if (assessmentError) throw assessmentError;
      
      // Get sender info
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', user?.id)
        .single();
      
      if (profileError && user) throw profileError;
      
      // Create sender name
      let senderName = 'RoundOne Team';
      if (profileData && user) {
        const firstName = profileData.first_name || '';
        const lastName = profileData.last_name || '';
        if (firstName || lastName) {
          senderName = `${firstName} ${lastName}`.trim();
        }
      }
      
      // Get organization name
      const organizationName = assessmentData?.organizations?.name || 'RoundOne AI';
      
      // Create assessment link token
      const token = encryptCandidateDetails({
        type: 'invite',
        assessmentId: candidate.assessment_id,
        candidateId: candidate.id,
        name: candidate.name,
        email: candidate.email
      });
      
      // Send invitation email
      const emailSuccess = await sendInvitationEmail(
        [{
          recipientEmail: candidate.email,
          recipientName: candidate.name,
          assessmentLink: `${window.location.origin}/assessment/${candidate.assessment_id}/landing?token=${token}`,
          companyName: organizationName
        }],
        candidate.assessmentTitle,
        senderName,
        candidate.assessment_id
      );
      
      if (emailSuccess) {
        toast.success('Invitation resent successfully');
      } else {
        throw new Error('Failed to send invitation');
      }
    } catch (error: any) {
      console.error('Error resending invitation:', error);
      toast.error(error.message || 'Failed to resend invitation');
    } finally {
      setResendingInvitation(false);
    }
  };

  const handleGetUpdates = async () => {
    try {
      setIsLoading(true);
      const { data: candidateData } = await serviceSupabase
        .from('candidates')
        .select('conversation_id')
        .eq('id', candidate.id)
        .single();

      if (!candidateData?.conversation_id) {
        toast.error('No conversation found for this candidate');
        return;
      }

      const { data, error } = await serviceSupabase.functions.invoke('recomplete-assessment', {
        body: {
          conversationId: candidateData.conversation_id,
          candidateId: candidate.id,
          assessmentId: candidate.assessment_id
        }
      });

      if (error) throw error;

      toast.success('Assessment updated successfully');
      // Refresh the candidate data
      onRefresh?.();
    } catch (error) {
      console.error('Error getting updates:', error);
      toast.error('Failed to get updates');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadgeColor = (status: ExtendedCandidate['status']) => {
    switch (status) {
      case 'invited':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'in_progress':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return '';
    }
  };

  const getStatusLabel = (status: ExtendedCandidate['status']) => {
    switch (status) {
      case 'invited':
        return 'Invited';
      case 'in_progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      default:
        return status;
    }
  };

  const getFitStatusBadgeColor = (status?: string | null) => {
    switch (status) {
      case 'good_fit':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'maybe':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'not_fit':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  
  const getFitStatusLabel = (status?: string | null) => {
    switch (status) {
      case 'good_fit':
        return 'Good Fit';
      case 'maybe':
        return 'Maybe';
      case 'not_fit':
        return 'Not Fit';
      default:
        return 'Not Rated';
    }
  };
  
  const handleUpdateFitStatus = async (status: string) => {
    if (!candidate || !candidate.id) return;
    
    try {
      setSavingFitStatus(true);
      
      // Convert "none" to null for database storage
      const fitStatus = status === "none" ? null : status;
      
      const { error } = await supabase
        .from('candidates')
        .update({ fit_status: fitStatus })
        .eq('id', candidate.id);
      
      if (error) throw error;
      
      // Update the local candidate object
      candidate.fit_status = fitStatus as 'good_fit' | 'maybe' | 'not_fit' | null;
      
      toast.success('Candidate fit status updated');
      
      // Refresh the candidate data if a refresh function was provided
      onRefresh?.();
    } catch (error: any) {
      console.error('Error updating fit status:', error);
      toast.error(error.message || 'Failed to update fit status');
    } finally {
      setSavingFitStatus(false);
    }
  };

  if (!candidate) return null;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-[40vw] !max-w-none overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Candidate Details</SheetTitle>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h3 className="text-lg font-semibold">{candidate.name}</h3>
                  <div className="flex items-center space-x-2">
                    <p className="text-sm text-muted-foreground">{candidate.email}</p>
                    <Badge 
                      variant="outline" 
                      className={getStatusBadgeColor(candidate.status)}
                    >
                      {getStatusLabel(candidate.status)}
                    </Badge>
                    {candidate.potentialPlagiarism && (
                      <Badge 
                        variant="destructive" 
                        className="flex items-center gap-1"
                      >
                        <Flag size={12} className="mr-1" />
                        Potential AI Usage
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex space-x-2">
                  {/* Push to ATS button - only show for completed assessments if candidate is from ATS */}
                  {candidate.status === 'completed' && candidate.ats_candidate_id && (
                    <Button 
                      variant="outline" 
                      size="icon" 
                      onClick={handlePushToATS}
                      disabled={pushingToATS}
                      title="Push to ATS"
                      className={pushingToATS ? 'opacity-50' : ''}
                    >
                      <CloudUpload className={`h-4 w-4 ${pushingToATS ? 'animate-bounce' : ''}`} />
                    </Button>
                  )}
                  <Button variant="outline" size="icon" onClick={handleShareAssessment} title="Copy share link">
                    <Share2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {/* Improved Fit Status UI */}
              {candidate.status === 'completed' && (
                <div className="mt-4">
                  <div className="flex items-center space-x-2 mb-1">
                    <div className="text-sm font-medium">Fit Status:</div>
                    <Select
                      value={candidate.fit_status || "none"}
                      onValueChange={handleUpdateFitStatus}
                      disabled={savingFitStatus}
                    >
                      <SelectTrigger className="border-none shadow-none p-0 h-auto w-auto font-medium">
                        <SelectValue>
                          {savingFitStatus ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Badge 
                              variant="outline" 
                              className={`${getFitStatusBadgeColor(candidate.fit_status)} ml-1 capitalize`}
                            >
                              {getFitStatusLabel(candidate.fit_status)}
                            </Badge>
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent align="start">
                        <SelectItem value="good_fit">
                          <Badge variant="outline" className={getFitStatusBadgeColor('good_fit')}>
                            Good Fit
                          </Badge>
                        </SelectItem>
                        <SelectItem value="maybe">
                          <Badge variant="outline" className={getFitStatusBadgeColor('maybe')}>
                            Maybe
                          </Badge>
                        </SelectItem>
                        <SelectItem value="not_fit">
                          <Badge variant="outline" className={getFitStatusBadgeColor('not_fit')}>
                            Not Fit
                          </Badge>
                        </SelectItem>
                        <SelectItem value="none">
                          <Badge variant="outline" className={getFitStatusBadgeColor(null)}>
                            Not Rated
                          </Badge>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              {candidate.status !== 'completed' && (
                <div className="mt-4">
                  <div className="flex items-center space-x-2 mb-1">
                    <div className="text-sm font-medium">Fit Status:</div>
                    <div className="text-sm text-muted-foreground">
                      Available after candidate completes assessment
                    </div>
                  </div>
                </div>
              )}
              
              {/* Hide Assessment line - commented out 
              <div className="flex items-center space-x-2 text-sm">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Assessment:</span>
                <span className="text-muted-foreground">{candidate.assessmentTitle}</span>
              </div>
              */}
              
              <Separator />
            </div>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {candidate.status === 'invited' && (
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-center text-center space-y-2 flex-col py-8">
                    <Mail className="h-12 w-12 text-blue-500" />
                    <div className="space-y-1">
                      <h3 className="text-lg font-medium">Invitation Sent</h3>
                      <p className="text-sm text-muted-foreground">
                        Waiting for the candidate to start the assessment
                      </p>
                      <Button 
                        variant="outline"
                        size="sm"
                        className="mt-4"
                        onClick={handleResendInvitation}
                        disabled={resendingInvitation}
                      >
                        {resendingInvitation ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            Resending...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Resend Invitation
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {candidate.status === 'in_progress' && (
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-center text-center space-y-2 flex-col py-8">
                    <Clock className="h-12 w-12 text-orange-500 animate-pulse" />
                    <div className="space-y-1">
                      <h3 className="text-lg font-medium">Assessment in Progress</h3>
                      <p className="text-sm text-muted-foreground">
                        The candidate is currently taking the assessment
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {candidate.status === 'completed' && !candidateAssessment?.audio_url && (
              <>
                <Card>
                  <CardContent className="pt-6">
                    <div className="space-y-6">
                      {/* Audio Player with Processing Message */}
                      <div className="rounded-lg border bg-card p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm font-medium">Assessment Recording</h3>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleOpenTranscript}
                            className="text-xs"
                          >
                            <FileText className="h-4 w-4 mr-2" />
                            View Transcript
                          </Button>
                        </div>
                        <div className="flex items-center justify-center text-center space-y-2 flex-col py-4 bg-muted/30 rounded-md">
                          <AlertCircle className="h-8 w-8 text-yellow-500 mb-2" />
                          <p className="text-sm text-muted-foreground">
                            Audio recording is being processed
                          </p>
                        </div>
                      </div>

                      {/* AI Evaluation Results */}
                      {(candidate.aiRating || candidate.ai_summary || candidate.skill_ratings) && (
                        <>
                          <AssessmentSummary
                            aiRating={candidate.aiRating || 0}
                            skillRatings={candidate.skill_ratings || []}
                            summary={candidate.ai_summary || undefined}
                          />

                          <PerformanceComparison
                            currentCandidateScore={(candidate.aiRating || 0) * 20}
                            assessmentId={candidate.assessment_id}
                            candidateId={candidate.id}
                          />
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {candidate.status === 'completed' && candidateAssessment?.audio_url && (
              <>
                <AssessmentRecording
                  assessmentTitle={candidate.assessmentTitle}
                  audioUrl={candidateAssessment.audio_url}
                  isPlaying={isPlaying}
                  isMuted={isMuted}
                  currentTime={currentTime}
                  duration={duration}
                  handlePlayPause={handlePlayPause}
                  handleVolumeToggle={handleVolumeToggle}
                  handleOpenTranscript={handleOpenTranscript}
                  handleSeek={handleSeek}
                  setDuration={setDuration}
                  setCurrentTime={setCurrentTime}
                />

                <AssessmentSummary
                  aiRating={candidate.aiRating || 0}
                  skillRatings={candidate.skill_ratings || []}
                  summary={candidate.ai_summary || undefined}
                />

                <PerformanceComparison
                  currentCandidateScore={(candidate.aiRating || 0) * 20}
                  assessmentId={candidate.assessment_id}
                  candidateId={candidate.id}
                />
              </>
            )}

            {(candidate.status === 'in_progress' || (candidate.status === 'completed' && !candidateAssessment?.audio_url)) && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleGetUpdates}
                disabled={isProcessingUpdate || isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Get Updates'
                )}
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <AssessmentTranscript
        isOpen={showTranscript}
        onClose={() => setShowTranscript(false)}
        transcript={candidateAssessment?.transcript}
      />
    </>
  );
};

export default CandidateSheet; 